import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatRupiah, formatDate } from "@/lib/format";
import { Wallet, Loader2, FileText, Receipt } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RupiahInput } from "@/components/RupiahInput";
import { Label } from "@/components/ui/label";
import { FileUploader } from "@/components/FileUploader";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { VehicleExpenseForm } from "@/components/VehicleExpenseForm";
import { VehicleCashHistory } from "@/components/VehicleCashHistory";
import { toast } from "sonner";

const EXPENSE_CATEGORIES = [
  "BBM / Bensin", "ATK & Perlengkapan", "Konsumsi / Rapat", "Transportasi",
  "Servis / Perbaikan", "Parkir & Tol", "Komunikasi / Pulsa", "Material / Barang", "Lain-lain",
];

export default function Expenses() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [generals, setGenerals] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  const [reqId, setReqId] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const [files, setFiles] = useState([]);
  const [amount, setAmount] = useState("");
  const [descChoice, setDescChoice] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/cash-requests").then((r) => setRequests(r.data)).catch(() => {}),
      api.get("/general-expenses").then((r) => setGenerals(r.data)).catch(() => {}),
      api.get("/vehicle-options").then((r) => setVehicles(r.data)).catch(() => {}),
    ]).finally(() => { setLoading(false); setReloadKey((k) => k + 1); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const isGeneral = reqId === "__general__";
  const requireVehicle = user?.role === "tim_kendaraan";
  // pengajuan yang sudah dicairkan & masih ada sisa saldo = ada kas yang bisa dibelanjakan
  const eligible = requests.filter((r) => r.status === "disbursed" && r.remaining_balance > 0);
  const selected = eligible.find((r) => r.id === reqId);
  const totalAvailable = eligible.reduce((s, r) => s + (r.remaining_balance || 0), 0);
  const maxAmount = isGeneral ? totalAvailable : (selected ? selected.remaining_balance : 0);

  // riwayat pengeluaran (bukti dari pengajuan + pengeluaran umum mandiri)
  const expenses = [];
  requests.forEach((r) => (r.expense_proofs || []).forEach((p) => {
    expenses.push({ ...p, source: p.general ? "Pengeluaran Umum" : "Dari: " + r.reason });
  }));
  generals.forEach((g) => expenses.push({ ...g, source: "Pengeluaran Umum" }));
  expenses.sort((a, b) => (b.uploaded_at || "").localeCompare(a.uploaded_at || ""));

  const submit = async (e) => {
    e.preventDefault();
    if (!reqId) { toast.error("Pilih pengajuan awal terlebih dahulu"); return; }
    if (requireVehicle && !vehicleId) { toast.error("Pilih mobil terlebih dahulu"); return; }
    if (!files.length) { toast.error("Upload bukti pengeluaran terlebih dahulu"); return; }
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error("Isi jumlah pengeluaran"); return; }
    if (!isGeneral && amt > maxAmount + 0.001) { toast.error(`Jumlah melebihi sisa kas tersedia (${formatRupiah(maxAmount)})`); return; }
    const finalDesc = descChoice === "__custom__" ? desc.trim()
      : descChoice === "__req__" ? (selected?.reason || "")
      : descChoice;
    if (!finalDesc) { toast.error("Pilih atau isi keterangan pengeluaran"); return; }
    setBusy(true);
    try {
      if (isGeneral) {
        await api.post("/general-expenses", {
          file_id: files[0].file_id, amount: amt, description: finalDesc, vehicle_id: vehicleId,
        });
      } else {
        await api.post(`/cash-requests/${reqId}/expense-proof`, {
          file_id: files[0].file_id, amount: amt, description: finalDesc, vehicle_id: vehicleId,
        });
      }
      toast.success("Pengeluaran berhasil dicatat.");
      setFiles([]); setAmount(""); setDesc(""); setDescChoice(""); setReqId(""); setVehicleId("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
          <Receipt className="h-6 w-6 text-brand" /> Pengeluaran Kas
        </h2>
        <p className="text-sm text-muted-foreground">{user?.role === "tim_kendaraan" ? "Catat pekerjaan & pengeluaran kendaraan — Cash (potong saldo kas) atau Tempo (tagihan)" : "Catat penggunaan kas dari pengajuan yang sudah dicairkan Tim Keuangan"}</p>
      </div>

      {/* Form buat pengeluaran */}
      <Card className="p-6">
        <h3 className="font-semibold mb-4" style={{ fontFamily: "Outfit" }}>Buat Pengeluaran</h3>
        {loading ? (
          <div className="space-y-2.5" data-testid="expenses-loading">{[0, 1, 2].map((i) => (<div key={i} className={`h-4 rounded-md skeleton-shimmer animate-fade-in-up stagger-${i + 1}`} />))}</div>
        ) : user?.role === "tim_kendaraan" ? (
          <VehicleExpenseForm vehicles={vehicles} user={user} onDone={load} />
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="expense-form">
            <div>
              <Label>Pengajuan Awal <span className="text-red-500">*</span></Label>
              <Select value={reqId} onValueChange={setReqId}>
                <SelectTrigger className="mt-1.5" data-testid="expense-request-select"><SelectValue placeholder="Pilih pengajuan atau Pengeluaran Umum" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__general__" data-testid="expense-req-general">Pengeluaran Umum (dari kas tersedia)</SelectItem>
                  {eligible.map((r) => (
                    <SelectItem key={r.id} value={r.id} data-testid={`expense-req-option-${r.id}`}>
                      {r.reason} — sisa {formatRupiah(r.remaining_balance)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {requireVehicle && (
            <div>
              <Label>Mobil / Kendaraan <span className="text-red-500">*</span></Label>
              <Select value={vehicleId} onValueChange={setVehicleId}>
                <SelectTrigger className="mt-1.5" data-testid="expense-vehicle-select"><SelectValue placeholder="Pilih plat nomor mobil" /></SelectTrigger>
                <SelectContent>
                  {vehicles.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-muted-foreground" data-testid="expense-no-vehicle">Belum ada data kendaraan</div>
                  ) : vehicles.map((v) => (
                    <SelectItem key={v.id} value={v.id} data-testid={`expense-vehicle-option-${v.id}`}>
                      {v.plate_number} — {v.vehicle_type}{v.branch ? ` (${v.branch})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            )}

            {isGeneral && (
              <div className="flex items-start gap-2 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5" data-testid="general-hint">
                <Receipt className="h-4 w-4 mt-0.5 shrink-0" />
                <span>Pengeluaran umum. Kas cair tersedia: <strong data-testid="general-available">{formatRupiah(totalAvailable)}</strong>. Akan diambil dari kas cair dulu; jika belum/kurang, sisanya tetap tercatat sebagai pengeluaran umum mandiri.</span>
              </div>
            )}

            {selected && (
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg bg-secondary p-3">
                  <p className="text-[11px] font-mono uppercase text-muted-foreground">Diterima</p>
                  <p className="font-mono font-semibold text-sm">{formatRupiah(selected.approved_amount)}</p>
                </div>
                <div className="rounded-lg bg-secondary p-3">
                  <p className="text-[11px] font-mono uppercase text-muted-foreground">Sudah Dibuktikan</p>
                  <p className="font-mono font-semibold text-sm text-emerald-700">{formatRupiah(selected.total_expensed)}</p>
                </div>
                <div className="rounded-lg bg-secondary p-3">
                  <p className="text-[11px] font-mono uppercase text-muted-foreground">Sisa Saldo</p>
                  <p className="font-mono font-semibold text-sm text-rose-600" data-testid="expense-remaining">{formatRupiah(selected.remaining_balance)}</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="eamt">Jumlah Pengeluaran (Rp) <span className="text-red-500">*</span></Label>
                <RupiahInput id="eamt" min="0" value={amount} onChange={(e) => setAmount(e.target.value)}
                  className="mt-1.5 font-mono" data-testid="expense-amount-input"
                  placeholder={maxAmount ? `maks ${maxAmount}` : "0"} />
              </div>
              <div>
                <Label>Keterangan Penggunaan <span className="text-red-500">*</span></Label>
                <Select value={descChoice} onValueChange={setDescChoice}>
                  <SelectTrigger className="mt-1.5" data-testid="expense-desc-select"><SelectValue placeholder="Pilih keterangan" /></SelectTrigger>
                  <SelectContent>
                    {selected && <SelectItem value="__req__" data-testid="expense-desc-fromreq">Sesuai pengajuan: {selected.reason}</SelectItem>}
                    {EXPENSE_CATEGORIES.map((c) => <SelectItem key={c} value={c} data-testid={`expense-desc-cat-${c}`}>{c}</SelectItem>)}
                    <SelectItem value="__custom__" data-testid="expense-desc-custom">Lainnya (tulis sendiri)</SelectItem>
                  </SelectContent>
                </Select>
                {descChoice === "__custom__" && (
                  <Input value={desc} onChange={(e) => setDesc(e.target.value)}
                    className="mt-2" data-testid="expense-desc-input" placeholder="Tulis keterangan pengeluaran..." />
                )}
              </div>
            </div>

            <div>
              <Label>Bukti Pengeluaran (foto struk / dokumen) <span className="text-red-500">*</span></Label>
              <div className="mt-1.5">
                <FileUploader files={files} onChange={setFiles} testId="expense-uploader" multiple={false} />
              </div>
            </div>

            <Button type="submit" disabled={busy} className="bg-brand hover:bg-emerald-800" data-testid="submit-expense-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Wallet className="h-4 w-4 mr-2" /> Catat Pengeluaran</>}
            </Button>
          </form>
        )}
      </Card>

      {user?.role === "tim_kendaraan" && <VehicleCashHistory reloadKey={reloadKey} />}

      {/* Riwayat pengeluaran */}
      <Card className="p-6">
        <h3 className="font-semibold mb-4" style={{ fontFamily: "Outfit" }}>Riwayat Pengeluaran</h3>
        {expenses.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Belum ada pengeluaran tercatat.</p>
        ) : (
          <ul className="space-y-2" data-testid="expense-history">
            {expenses.map((p, i) => (
              <li key={`exp-${i}`} className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border" data-testid={`expense-item-${i}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{p.description || "Pengeluaran"}</p>
                  <p className="text-xs text-muted-foreground truncate">{p.plate_number ? `${p.plate_number} · ` : ""}{p.source} · {formatDate(p.uploaded_at)}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <a href={fileUrl(p.file_id)} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-brand hover:underline text-sm" data-testid={`expense-proof-link-${i}`}>
                    <FileText className="h-4 w-4" /> Bukti
                  </a>
                  <span className="font-mono text-sm font-semibold">{formatRupiah(p.amount)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
